import { isCatalogItemId, RESULT_RANGE } from "@loot-os/api-contract";

/**
 * The drop-table import: rows pasted from a sheet, or a CSV file, one item per line as
 * `id <tab or comma> weight`. The id is the catalog item id, which for anything migrated from
 * boxed is the asset group id the team's sheets already carry (the "Retool ID").
 *
 * The weight's unit is read from what was pasted: a `%` on any line means percent; otherwise a
 * total of about 100 is percent and a total of exactly 1,000,000 is parts per million. Anything
 * else is ambiguous, and the dialog asks. A line with no weight imports at 0, to be weighed in
 * the editor.
 */

/** The most rows one publish accepts (`publishVersionInput`). */
export const MAX_IMPORT_ROWS = 500;

export type WeightUnit = "percent" | "ppm";

export const IMPORT_ISSUE_REASONS = ["bad_weight", "duplicate_id", "too_many_rows"] as const;
export type ImportIssueReason = (typeof IMPORT_ISSUE_REASONS)[number];

export const IMPORT_ISSUE_MESSAGES: Record<ImportIssueReason, string> = {
  bad_weight: "the weight is not a number of zero or more",
  duplicate_id: "this id is already on an earlier line",
  too_many_rows: `a drop table holds at most ${MAX_IMPORT_ROWS} rows`,
};

export interface ImportIssue {
  /** 1-based, as a sheet numbers its rows. */
  line: number;
  reason: ImportIssueReason;
  text: string;
}

export interface ImportedRow {
  id: string;
  /** In the unit `unit` names, as typed. */
  weight: number;
}

export interface ParsedImport {
  rows: ImportedRow[];
  /** null when the weights could be either and nobody said which. */
  unit: WeightUnit | null;
  issues: ImportIssue[];
}

/** A total this close to 100 is read as percent: sheets round each row. */
const PERCENT_TOLERANCE = 1;

function splitLine(line: string): string[] {
  const cells = line.includes("\t") ? line.split("\t") : line.split(",");
  return cells.map((cell) => cell.trim().replace(/^"(.*)"$/, "$1"));
}

/** A first line whose weight cell is words, not a number: a sheet's header row. */
function isHeader(cells: string[]): boolean {
  const [id, weight] = cells;
  if (id === undefined || isCatalogItemId(id)) return false;
  return weight !== undefined && weight !== "" && Number.isNaN(Number.parseFloat(weight));
}

function inferUnit(rows: ImportedRow[], sawPercentSign: boolean): WeightUnit | null {
  if (sawPercentSign) return "percent";
  const total = rows.reduce((sum, row) => sum + row.weight, 0);
  if (total === 0) return "percent";
  if (Math.abs(total - 100) <= PERCENT_TOLERANCE) return "percent";
  if (total === RESULT_RANGE) return "ppm";
  return null;
}

/** Parses pasted text. `unit` overrides the inference, once the operator has said which. */
export function parseDropTableImport(text: string, unit?: WeightUnit): ParsedImport {
  const rows: ImportedRow[] = [];
  const issues: ImportIssue[] = [];
  const seen = new Set<string>();
  let sawPercentSign = false;

  const lines = text.split(/\r?\n/);
  for (const [index, raw] of lines.entries()) {
    const line = raw.trim();
    if (line === "") continue;
    const cells = splitLine(line);
    if (index === 0 && isHeader(cells)) continue;
    const [id = "", weightCell = ""] = cells;
    if (id === "") continue;

    const weightText = weightCell.replace(/%$/, "").replaceAll(",", "").trim();
    if (weightCell.endsWith("%")) sawPercentSign = true;
    const weight = weightText === "" ? 0 : Number(weightText);
    const reason: ImportIssueReason | null =
      !Number.isFinite(weight) || weight < 0
        ? "bad_weight"
        : seen.has(id.toLowerCase())
          ? "duplicate_id"
          : rows.length >= MAX_IMPORT_ROWS
            ? "too_many_rows"
            : null;
    if (reason) {
      issues.push({ line: index + 1, reason, text: line });
      continue;
    }
    seen.add(id.toLowerCase());
    rows.push({ id, weight });
  }

  return { rows, unit: unit ?? inferUnit(rows, sawPercentSign), issues };
}

/** One imported weight as the percent the editor's weight field holds. */
export function importedWeightAsPercent(weight: number, unit: WeightUnit): string {
  const percent = unit === "percent" ? weight : (weight / RESULT_RANGE) * 100;
  return String(Number(percent.toFixed(4)));
}

/** The ids worth asking the catalog about: only a uuid can name a catalog item. */
export function lookupIds(rows: ImportedRow[]): string[] {
  return rows.map((row) => row.id).filter(isCatalogItemId);
}
